// @flow
import * as React from 'react'
import shallowEqual from 'mz-utils/shallowEqual'
import { FormattedMessage } from 'mz-intl'
import RadioField from 'mz-ui-kit/form/RadioField'
import type { Input, Meta } from 'mz-ui-kit/core/BaseField'
import type { PaymentOption } from 'mz-sdk/services/creditcard/getCreditCards'
import type { MethodProps } from '..'
import messages from '../messages.intl'
import createPaymentIntent from 'mz-sdk/services/payment/createPaymentIntent'
import CreditcardIcon from '../assets/creditcard'
import { createAuthorizedPaymentIntent } from '../stripe'
import {
  PAY_WITH_OPTION,
  SUPPORTED_PROVIDERS,
  ASYNC_PAYMENT_METHOD,
  PAY_WITH_INTENT,
} from '../constants'
import {
  Container,
  BrandContainer,
  Label,
  LabelValue,
  LabelsWrapper,
  LabelName,
  CardHolderWrapper,
  CardExpiryWrapper,
} from './elements'

// Types
export type Props = {
  activeValue?: Object,
  activeValueComparator?: Function,
  input: Input,
  meta: Meta,
  option: PaymentOption,
  disabled?: boolean,
  methodProps: MethodProps,
}

/**
 * Handle the case when the option has paymentmethod_id, which indicates
 * that we should go through the PaymentIntent flow.
 *
 * @param {PaymentOption} option
 * @param {MethodProps} methodProps
 */
async function authorizePaymentIntent(
  option: PaymentOption,
  methodProps: MethodProps
) {
  const intentData = await createPaymentIntent({
    search_id: methodProps.searchId,
    result_id: methodProps.tripId,
    amount: methodProps.stripeChargeAmount,
    currency: methodProps.currency,
    stripe_origin: methodProps.config.origin,
    customer_id: option.stripe_customer_id,
  })
  const completeIntent = await createAuthorizedPaymentIntent({
    stripe: methodProps.stripe,
    paymentMethodId: option.paymentmethod_id,
    paymentIntentData: intentData,
  })
  return {
    type: PAY_WITH_INTENT,
    intent: completeIntent,
    origin: methodProps.config.origin,
    currency: methodProps.currency,
  }
}

/**
 * General reponsive card elemenent for showing payment option
 * @param  {Object} props.option   A payment option object
 * @return {React.Element}
 */
const CardPaymentOption = ({
  activeValue,
  activeValueComparator,
  option,
  disabled,
  methodProps,
  ...rest
}: Props) => {
  const methodPropsRef = React.useRef(methodProps)
  methodPropsRef.current = methodProps

  const getOption = React.useCallback(async () => {
    if (!option.paymentmethod_id || methodPropsRef.current.postPayment) {
      return {
        type: PAY_WITH_OPTION,
        option,
        brand: option.brand,
      }
    }
    return authorizePaymentIntent(option, methodPropsRef.current)
  }, [option, methodPropsRef])

  const activeVal = activeValue || {
    type: ASYNC_PAYMENT_METHOD,
    brand: option.brand,
    getOption,
  }
  const active =
    !disabled &&
    !!rest.input &&
    ((activeValueComparator &&
      activeValueComparator(rest.input.value, activeVal)) ||
      shallowEqual(rest.input.value, activeVal))
  const stylishNumber = option.number
    ? option.number.replace(/\*/g, '•')
    : '•••• •••• •••• ••••'

  const BrandIconCompoenet =
    (option.brand && SUPPORTED_PROVIDERS[option.brand]) || CreditcardIcon

  return (
    <Container $active={active} $disabled={disabled}>
      <RadioField
        {...rest}
        activeValue={activeVal}
        inactiveValue={null}
        activeValueComparator={activeValueComparator}
        disabled={disabled}
      />
      <BrandContainer>
        <BrandIconCompoenet />
      </BrandContainer>
      <LabelsWrapper>
        <Label>
          <LabelName>
            <FormattedMessage message={messages.CARD_TITLE} />
          </LabelName>
          <LabelValue>{stylishNumber}</LabelValue>
        </Label>
        {!!option.name && (
          <CardHolderWrapper>
            <LabelName>
              <FormattedMessage message={messages.CARD_HOLDER_NAME} />
            </LabelName>
            <LabelValue>{option.name}</LabelValue>
          </CardHolderWrapper>
        )}
        {option.exp_month && option.exp_year && (
          <CardExpiryWrapper>
            <LabelName>
              <FormattedMessage message={messages.CARD_EXPIRY} />
            </LabelName>
            <LabelValue>
              {option.exp_month} / {option.exp_year}
            </LabelValue>
          </CardExpiryWrapper>
        )}
      </LabelsWrapper>
    </Container>
  )
}

export default CardPaymentOption
